// store.jsx — CardResultsContext (resultado vigente + histórico)
import { createContext } from "preact";
import { useContext, useMemo } from "preact/hooks";
import { useQuotationByIdMultiRole } from "../store";

export const CardResultsContext = createContext(null);

export function useCardResults() {
  const ctx = useContext(CardResultsContext);
  if (!ctx) throw new Error("useCardResults must be used within CardResultsProvider");
  return ctx;
}

// 1) extrae cualquier shape (result | results | results.items) a array plano
function extractResults(q) {
  if (!q) return [];
  const r = q.results ?? q.result ?? null;
  if (Array.isArray(r)) return r.filter(Boolean);
  if (r && Array.isArray(r.items)) return r.items.filter(Boolean);
  if (r && typeof r === "object") return [r];
  return [];
}

// 2) normaliza cada item a un shape estable para el UI
function normalizeItem(x) {
  const dev = Number(x?.estimatedDevCost || 0);
  const infra = Number(x?.estimatedManufactureCost || 0);

  const updatedAt = x?.updatedAt || x?.updated_at || null;
  const createdAt = x?.createdAt || x?.created_at || null;

  return {
    id: x?._id || x?.id || null,
    notes: String(x?.notes || "").trim(),
    availableStartDate: x?.availableStartDate || null,
    estimatedTimeDays: Number(x?.estimatedTimeDays || 0),
    estimatedDevCost: dev,
    estimatedInfraCost: infra, // renombrado para claridad de UI
    currency: x?.currency || "MXN",
    updatedAt,
    createdAt,
    // calculado:
    costTotal: dev + infra,
    // opcional:
    category: x?.category || "General",
  };
}

// 3) ordena por vigencia (updatedAt DESC, fallback createdAt DESC)
function sortByRecency(a, b) {
  const ua = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
  const ub = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
  if (ua !== ub) return ub - ua;
  const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
  const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
  return cb - ca;
}

export function CardResultsProvider({ children }) {
  const { quotation, role } = useQuotationByIdMultiRole();

  // Todos los resultados normalizados y ordenados por más reciente
  const all = useMemo(() => {
    const raw = extractResults(quotation).map(normalizeItem);
    return raw.sort(sortByRecency);
  }, [quotation]);

  // Vigente = el más reciente; histórico = el resto
  const current = all[0] || null;
  const history = all.length > 1 ? all.slice(1) : [];

  // Totales calculados **sólo del vigente**
  const totals = useMemo(() => {
    if (!current) return { currency: "MXN", costTotal: 0, days: 0 };
    return {
      currency: current.currency || "MXN",
      costTotal: current.costTotal || 0,
      days: current.estimatedTimeDays || 0,
    };
  }, [current]);

  return (
    <CardResultsContext.Provider
      value={{ quotation, role, current, history, totals }}
    >
      {children}
    </CardResultsContext.Provider>
  );
}
